import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  User,
  Mail,
  Phone,
  Lock,
  Building2,
  MapPin,
  Shield,
  Camera,
  FileCheck,
  Upload,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Eye,
  EyeOff,
  AlertCircle,
  BadgeCheck,
  FileText,
  MessageSquare
} from 'lucide-react';

const RegistroInstitucionalPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    password: '',
    confirmPassword: '',
    company: '',
    location: ''
  });
  
  // Verification
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [codeExpires, setCodeExpires] = useState(null);
  const codeInputRefs = useRef([]);
  
  // Documents
  const [idDocument, setIdDocument] = useState(null);
  const [idDocumentType, setIdDocumentType] = useState('ine');
  const [selfie, setSelfie] = useState(null);
  
  // Ethics
  const [ethicsAccepted, setEthicsAccepted] = useState(false);
  const [manualAccepted, setManualAccepted] = useState(false);
  
  const steps = [
    { number: 1, title: 'Datos Personales', icon: User },
    { number: 2, title: 'Verificar Email', icon: Mail },
    { number: 3, title: 'Identificación', icon: FileCheck },
    { number: 4, title: 'Selfie', icon: Camera },
    { number: 5, title: 'Código de Ética', icon: Shield }
  ];

  useEffect(() => {
    // If user is authenticated and on later steps, continue
    if (isAuthenticated && user) {
      // Check verification status
      checkVerificationStatus();
    }
  }, [isAuthenticated, user]);

  const checkVerificationStatus = async () => {
    try {
      const response = await api.get('/verification/status');
      const status = response.data;
      
      // Determine which step to show based on verification status
      if (!status.email_verified) {
        setStep(2);
      } else if (!status.documents_submitted) {
        if (!user?.id_document_url) {
          setStep(3);
        } else {
          setStep(4);
        }
      } else if (!status.ethics_signed) {
        setStep(5);
      } else {
        // Fully verified, redirect to dashboard
        navigate('/dashboard/invitado');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-fill WhatsApp with phone if empty
    if (name === 'phone' && !formData.whatsapp) {
      setFormData(prev => ({ ...prev, whatsapp: value }));
    }
  };

  // Step 1: Register
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({ title: "Error", description: "Las contraseñas no coinciden", variant: "destructive" });
      return;
    }
    
    if (formData.phone.length < 10) {
      toast({ title: "Error", description: "El número de celular debe tener al menos 10 dígitos", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        whatsapp: formData.whatsapp || formData.phone,
        password: formData.password,
        company: formData.company,
        location: formData.location
      });
      
      // Login the user
      login(response.data.access_token, response.data.user);
      
      toast({
        title: "¡Bienvenido a UPIM!",
        description: "Te hemos enviado un código de verificación a tu correo"
      });
      
      setCodeExpires(Date.now() + 15 * 60 * 1000); // 15 minutes
      setStep(2);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al registrar",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify Email
  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      value = value[0];
    }
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyEmail = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      toast({ title: "Error", description: "Ingresa el código de 6 dígitos", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/verification/email/verify', {
        email: formData.email || user?.email,
        code: code
      });
      
      toast({ title: "¡Email verificado!", description: "Ahora sube tu identificación oficial" });
      setStep(3);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Código incorrecto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await api.post('/verification/email/send-code', {
        email: formData.email || user?.email
      });
      setCodeExpires(Date.now() + 15 * 60 * 1000);
      setVerificationCode(['', '', '', '', '', '']);
      toast({ title: "Código reenviado", description: "Revisa tu correo electrónico" });
    } catch (error) {
      toast({ title: "Error", description: "No se pudo reenviar el código", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Upload ID Document
  const handleIdUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Error", description: "El archivo es muy grande. Máximo 10MB", variant: "destructive" });
      return;
    }
    
    setIdDocument(file);
    setLoading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', idDocumentType);
      
      await api.post('/verification/documents/id', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast({ title: "Identificación cargada", description: "Ahora toma una selfie de verificación" });
      setStep(4);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al subir el documento",
        variant: "destructive"
      });
      setIdDocument(null);
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Upload Selfie
  const handleSelfieUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Error", description: "El archivo es muy grande. Máximo 10MB", variant: "destructive" });
      return;
    }
    
    setSelfie(file);
    setLoading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      await api.post('/verification/documents/selfie', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast({ title: "Selfie cargada", description: "Ahora firma el Código de Ética" });
      setStep(5);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al subir la selfie",
        variant: "destructive"
      });
      setSelfie(null);
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Sign Ethics
  const handleSignEthics = async () => {
    if (!ethicsAccepted || !manualAccepted) {
      toast({ title: "Error", description: "Debes aceptar ambos documentos", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/verification/ethics/sign', {
        accept_ethics_code: ethicsAccepted,
        accept_affiliation_manual: manualAccepted
      });
      
      toast({
        title: "¡Registro completado!",
        description: "Bienvenido a UPIM como Invitado Institucional"
      });
      
      navigate('/dashboard/invitado');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al firmar los documentos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Registro Institucional UPIM
            </h1>
            <p className="text-gray-600">
              Únete como Invitado Institucional y accede a contenido estratégico curado
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-between items-center mb-8 relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10" />
            {steps.map((s, index) => {
              const Icon = s.icon;
              const isActive = step === s.number;
              const isCompleted = step > s.number;
              
              return (
                <div key={s.number} className="flex flex-col items-center relative z-10">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-all
                    ${isCompleted ? 'bg-green-600 text-white' : 
                      isActive ? 'bg-gray-900 text-white ring-4 ring-gray-900/20' : 
                      'bg-gray-200 text-gray-500'}
                  `}>
                    {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className={`text-xs mt-2 text-center max-w-[80px] ${isActive ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step Content */}
          <Card className="shadow-xl border-0">
            <CardContent className="p-8">
              
              {/* Step 1: Personal Data */}
              {step === 1 && (
                <form onSubmit={handleRegister} className="space-y-6">
                  <div className="text-center mb-6">
                    <User className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Información Personal</h2>
                    <p className="text-gray-500 text-sm">Todos los campos marcados son obligatorios</p>
                  </div>
                  
                  <div className="grid gap-4">
                    <div>
                      <Label htmlFor="name">Nombre completo *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Ej: Juan Pérez García"
                        required
                        data-testid="register-name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Correo electrónico *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="correo@ejemplo.com"
                        required
                        data-testid="register-email"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Celular *</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="55 1234 5678"
                            className="pl-10"
                            required
                            data-testid="register-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="whatsapp">WhatsApp *</Label>
                        <div className="relative">
                          <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="whatsapp"
                            name="whatsapp"
                            value={formData.whatsapp}
                            onChange={handleInputChange}
                            placeholder="55 1234 5678"
                            className="pl-10"
                            required
                            data-testid="register-whatsapp"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="password">Contraseña *</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Mínimo 8 caracteres"
                            className="pl-10 pr-10"
                            minLength={8}
                            required
                            data-testid="register-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="confirmPassword">Confirmar *</Label>
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Repite la contraseña"
                          required
                          data-testid="register-confirm-password"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="company">Empresa / Inmobiliaria (opcional)</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Nombre de tu empresa"
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="location">Ciudad / Estado (opcional)</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="Ej: Ciudad de México"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gray-900 hover:bg-gray-800"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Continuar
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                  
                  <p className="text-center text-sm text-gray-500">
                    ¿Ya tienes cuenta?{' '}
                    <Link to="/login" className="text-gray-900 font-medium hover:underline">
                      Inicia sesión
                    </Link>
                  </p>
                </form>
              )}
              
              {/* Step 2: Email Verification */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Verifica tu correo</h2>
                    <p className="text-gray-500 text-sm mt-2">
                      Enviamos un código de 6 dígitos a<br />
                      <span className="font-medium text-gray-900">{formData.email || user?.email}</span>
                    </p>
                  </div>
                  
                  <div className="flex justify-center gap-2">
                    {verificationCode.map((digit, index) => (
                      <Input
                        key={index}
                        ref={el => codeInputRefs.current[index] = el}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        className="w-12 h-14 text-center text-2xl font-bold"
                        maxLength={1}
                        inputMode="numeric"
                        data-testid={`verification-code-${index}`}
                      />
                    ))}
                  </div>
                  
                  <Button 
                    onClick={handleVerifyEmail}
                    className="w-full h-12 bg-gray-900 hover:bg-gray-800"
                    disabled={loading || verificationCode.join('').length !== 6}
                    data-testid="verify-email-submit"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verificar código'}
                  </Button>
                  
                  <p className="text-center text-sm text-gray-500">
                    ¿No recibiste el código?{' '}
                    <button 
                      onClick={handleResendCode}
                      className="text-gray-900 font-medium hover:underline"
                      disabled={loading}
                    >
                      Reenviar
                    </button>
                  </p>
                </div>
              )}
              
              {/* Step 3: ID Document */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <FileCheck className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Identificación Oficial</h2>
                    <p className="text-gray-500 text-sm">
                      Sube una foto clara de tu INE, pasaporte o identificación oficial
                    </p>
                  </div>
                  
                  <div className="flex gap-4 justify-center mb-4">
                    {['ine', 'pasaporte'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setIdDocumentType(type)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          idDocumentType === type 
                            ? 'border-gray-900 bg-gray-900 text-white' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {type === 'ine' ? 'INE' : 'Pasaporte'}
                      </button>
                    ))}
                  </div>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleIdUpload}
                      className="hidden"
                      id="id-upload"
                      data-testid="id-upload-input"
                    />
                    <label htmlFor="id-upload" className="cursor-pointer">
                      {idDocument ? (
                        <div className="flex items-center justify-center gap-3">
                          <CheckCircle2 className="h-8 w-8 text-green-600" />
                          <span className="text-green-600 font-medium">{idDocument.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-600 font-medium">Haz clic para subir tu identificación</p>
                          <p className="text-gray-400 text-sm mt-1">JPG, PNG o PDF (máx. 10MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                  
                  {loading && (
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Subiendo documento...</span>
                    </div>
                  )}
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium">Asegúrate de que:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>El documento esté completo y legible</li>
                          <li>No haya reflejos ni sombras</li>
                          <li>La información sea visible</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 4: Selfie */}
              {step === 4 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <Camera className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Verificación Facial</h2>
                    <p className="text-gray-500 text-sm">
                      Toma una selfie sosteniendo tu identificación junto a tu rostro
                    </p>
                  </div>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={handleSelfieUpload}
                      className="hidden"
                      id="selfie-upload"
                      data-testid="selfie-upload-input"
                    />
                    <label htmlFor="selfie-upload" className="cursor-pointer">
                      {selfie ? (
                        <div className="flex items-center justify-center gap-3">
                          <CheckCircle2 className="h-8 w-8 text-green-600" />
                          <span className="text-green-600 font-medium">Selfie cargada</span>
                        </div>
                      ) : (
                        <>
                          <Camera className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-600 font-medium">Toma o sube tu selfie</p>
                          <p className="text-gray-400 text-sm mt-1">Sostén tu ID junto a tu rostro</p>
                        </>
                      )}
                    </label>
                  </div>
                  
                  {loading && (
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Subiendo imagen...</span>
                    </div>
                  )}
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Camera className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium">Consejos para una buena selfie:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>Buena iluminación (luz frontal)</li>
                          <li>Rostro e ID visibles</li>
                          <li>Sin filtros ni ediciones</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 5: Ethics */}
              {step === 5 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Código de Ética UPIM</h2>
                    <p className="text-gray-500 text-sm">
                      Lee y acepta los documentos para completar tu registro
                    </p>
                  </div>
                  
                  {/* Ethics Document */}
                  <div className="border rounded-xl p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-6 w-6 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">Código de Honor y Justicia UPIM</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Principios éticos y de conducta profesional que rigen a los miembros de UPIM
                        </p>
                        <a 
                          href="https://upim.org/wp-content/uploads/2023/10/CODIGO-HONOR-Y-JUSTICIA-UPIM.doc.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                        >
                          Ver documento completo →
                        </a>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-3 pl-16">
                      <Checkbox
                        id="ethics"
                        checked={ethicsAccepted}
                        onCheckedChange={setEthicsAccepted}
                        data-testid="ethics-checkbox"
                      />
                      <label htmlFor="ethics" className="text-sm text-gray-700 cursor-pointer">
                        He leído y acepto el Código de Honor y Justicia UPIM
                      </label>
                    </div>
                  </div>
                  
                  {/* Affiliation Manual */}
                  <div className="border rounded-xl p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <BadgeCheck className="h-6 w-6 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">Manual de Afiliación UPIM</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Reglamento interno, derechos y obligaciones del afiliado
                        </p>
                        <a 
                          href="https://upim.org/wp-content/uploads/2023/10/MANUAL-DE-AFILIACION-UPIM-2023.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                        >
                          Ver documento completo →
                        </a>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-3 pl-16">
                      <Checkbox
                        id="manual"
                        checked={manualAccepted}
                        onCheckedChange={setManualAccepted}
                        data-testid="manual-checkbox"
                      />
                      <label htmlFor="manual" className="text-sm text-gray-700 cursor-pointer">
                        He leído y acepto el Manual de Afiliación UPIM
                      </label>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleSignEthics}
                    className="w-full h-12 bg-gray-900 hover:bg-gray-800"
                    disabled={loading || !ethicsAccepted || !manualAccepted}
                    data-testid="sign-ethics-submit"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-5 w-5" />
                        Completar Registro
                      </>
                    )}
                  </Button>
                  
                  <p className="text-center text-xs text-gray-400">
                    Al firmar, se registrará tu dirección IP y fecha de aceptación
                  </p>
                </div>
              )}
              
            </CardContent>
          </Card>

          {/* Trust badges */}
          <div className="mt-8 flex justify-center items-center gap-6 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>Datos protegidos</span>
            </div>
            <div className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4" />
              <span>Verificación segura</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RegistroInstitucionalPage;
